import { type DeepKeys } from "@tanstack/react-form";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";

import type { FormApi } from "./types";

import { FormFieldWrapper } from "./form-field-wrapper";
import { maskValue } from "./mask-value";

/* eslint-disable @typescript-eslint/no-explicit-any */
type FormInputProps<TFormData> = {
  form: FormApi<TFormData>;
  name: DeepKeys<TFormData>;
  label: string;
  placeholder?: string;
  type?:
    | "text"
    | "email"
    | "password"
    | "secret"
    | "url"
    | "number";
  required?: boolean;
  description?: string;
  className?: string;
  autoComplete?: string;
  disabled?: boolean;
};

export function FormInput<TFormData>({
  form,
  name,
  label,
  placeholder,
  type = "text",
  required = false,
  description,
  className,
  autoComplete,
  disabled,
}: FormInputProps<TFormData>) {
  const [isVisible, setIsVisible] = useState(false);

  const isPassword = type === "password";
  const isSecret = type === "secret";
  const isProtected = isPassword || isSecret;
  const isNumber = type === "number";

  // password: always use native masking, secret: use custom masking when hidden
  const inputType = isPassword
    ? isVisible
      ? "text"
      : "password"
    : isNumber
      ? "number"
      : "text";

  const autocompleteValue =
    autoComplete ??
    (type === "email"
      ? "email"
      : type === "password"
        ? "current-password"
        : type === "secret"
          ? "off"
          : undefined);

  return (
    <FormFieldWrapper
      form={form}
      name={name}
      label={label}
      required={required}
      description={description ?? null}
      className={className ?? null}
    >
      {(field, isInvalid) => (
        <div className="relative">
          {isSecret && !isVisible ? (
            <div
              id={field.name}
              className="flex h-8 w-full items-center rounded-none border border-input bg-transparent px-2.5 py-1 pr-10 text-xs"
              aria-label="Hidden secret value"
            >
              {maskValue(
                (field.state.value as string) ?? "",
                "input"
              )}
            </div>
          ) : (
            <Input
              id={field.name}
              name={field.name}
              type={inputType}
              placeholder={placeholder}
              value={(field.state.value as string) ?? ""}
              onChange={(e) => {
                // For number inputs, convert empty string to undefined to avoid sending 0
                const value = e.target.value;
                if (isNumber && value === "") {
                  field.handleChange(undefined as any);
                } else {
                  field.handleChange(value as any);
                }
              }}
              onBlur={field.handleBlur}
              autoComplete={autocompleteValue}
              aria-invalid={isInvalid}
              className={
                /* oxlint-disable-next-line shadcn/no-restyle -- one-off page treatment preserves the existing component appearance; no shared variant covers this use. */
                isProtected ? "pr-10" : ""
              }
              disabled={disabled}
            />
          )}

          {isProtected && (
            <Button
              size="icon"
              type="button"
              variant="transparent"
              spacing="padded"
              className="absolute top-0 right-0 h-full"
              onClick={() => setIsVisible(!isVisible)}
            >
              {isVisible ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      )}
    </FormFieldWrapper>
  );
}
